import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { StripeProvider, Elements, injectStripe } from 'react-stripe-elements';
import { isRunningInProduction, getIP } from '../utils';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { callFunction } from '../utils/FirebaseUtils';
import User from '../data/User';
import CountySelect from './components/CountrySelect';
import { reportBFError } from '../utils/Tracker';

const KEY = isRunningInProduction()
  ? 'pk_live_Jtt6CpKvT19xB2Ngo99mdzlh'
  : 'pk_test_59pGhOCyMYHL2fJGJlNYjEOC';

interface FormProps {
  stripe?: any;
}

const style: any = {
  base: {
    fontSize: '18px',
    color: '#424770',
    letterSpacing: '0.025em',
    fontFamily: 'Source Code Pro, monospace',
    '::placeholder': {
      color: '#aab7c4'
    }
    // padding: 10
  },
  invalid: {
    color: '#9e2146'
  }
};

const StyledForm = styled.form`
  form {
    margin-bottom: 40px;
    padding-bottom: 40px;
    border-bottom: 3px solid #e6ebf1;
  }

  input,
  select,
  .StripeElement {
    display: block;
    /* margin: 10px 0 20px 0; */
    width: 400px;
    max-width: 500px;
    padding: 10px 14px;
    font-size: 18px;
    font-family: 'Source Code Pro', monospace;
    box-shadow: rgba(50, 50, 93, 0.14902) 0px 1px 3px,
      rgba(0, 0, 0, 0.0196078) 0px 1px 0px;
    border: 0;
    outline: 0;
    border-radius: 4px;
    background: white;
  }

  .ElementsApp input {
    background: red;
  }

  .taxId {
    height: 37px;
  }

  input::placeholder {
    color: #aab7c4;
  }

  input:focus,
  .StripeElement--focus {
    box-shadow: rgba(50, 50, 93, 0.109804) 0px 4px 6px,
      rgba(0, 0, 0, 0.0784314) 0px 1px 3px;
    -webkit-transition: all 150ms ease;
    transition: all 150ms ease;
  }

  .StripeElement.IdealBankElement,
  .StripeElement.PaymentRequestButton {
    padding: 0;
  }

  h1 {
    margin-bottom: 20px;
  }

  hr {
    margin-bottom: 15px;
    border: none;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const Label = styled.span`
  display: block;
  text-align: left;
  margin-bottom: 5px;
`;

const FormRow = styled.div`
  margin-bottom: 20px;
  label {
    display: block;
  }
  .row {
    display: flex;
    text-align: left;

    input,
    select {
      width: 195px;

      &:first-child {
        margin-right: 10px;
      }
    }

    select {
      /* color: red; */
      &:invalid {
        color: green;
      }
      &:invalid > option:not(:first-child),
      optgroup {
        color: initial;
      }
      /* option:first-child {
        color: #aab7c4;
      }
      option[value=''] {
        background: red;
        color: #aab7c4;
      }

      &:selected {
        color: red;
      } */
    }
  }

  .footer {
    font-size: 14px;
    line-height: 14px;
    color: rgba(var(--primary-fg-rgb), 0.7);
    margin-top: 10px;
    text-align: left;
  }
`;

interface State {
  isProcessing: boolean;
}

class Form extends React.Component<FormProps, State> {
  state = {
    isProcessing: false
  };
  handleSubmit = async (ev: any) => {
    ev.preventDefault();

    const postalCode = this.getPostalCode();
    if (!postalCode) {
      alert('Please add postal code');
      return;
    }

    if (!this.props.stripe) {
      console.log("Stripe.js hasn't loaded yet.");
      return;
    }
    this.setState({ isProcessing: true });
    try {
      const user = getStoreState().app.user!;
      if (user.customerId) {
        alert('Add card should be done in Stripe');
      } else {
        const userData = await this.registerCustomer();
        storeDispatch(Actions.updateUser(userData));
      }
    } catch (e) {
      reportBFError(e);
      this.setState({ isProcessing: false }, () => {
        storeDispatch(Actions.showError(e));
      });
    }
  };
  getAddressCountry(): string {
    return (
      (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
        'select'
      ) as HTMLSelectElement
    ).value;
  }
  getPostalCode(): string | null {
    const result = (
      (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
        '.postalCode'
      ) as HTMLInputElement
    ).value.trim();
    if (result.length === 0) {
      return null;
    }
    return result;
  }

  async registerCustomer(): Promise<User> {
    const addressCountry = this.getAddressCountry();
    const postalCode = this.getPostalCode()!;
    const ipAddress = await getIP();
    const data: any = {
      addressCountry,
      postalCode,
      ipAddress
    };
    const taxId = (
      (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
        '.taxId'
      ) as HTMLInputElement
    ).value.trim();
    if (taxId.length > 0) {
      data.taxId = taxId;
    }
    const user = await callFunction('createCustomerWithoutCard', data);
    return user.data;
  }

  render() {
    const user = getStoreState().app.user;
    if (!user) {
      return null;
    }
    return (
      <StyledForm onSubmit={this.handleSubmit} style={style}>
        <h1>Add billing information</h1>
        <FormRow>
          <Label>Address:</Label>
          <div className="row">
            <CountySelect initialValue={user.countryCode} />
            <input
              className="postalCode"
              type="text"
              placeholder="Postal code"
            />
          </div>
        </FormRow>
        <FormRow>
          <Label>Tax ID / VAT N. (only for businesses)</Label>
          <input className="taxId" type="text" placeholder="Tax ID or VAT N." />
        </FormRow>

        <button
          className="large primary"
          style={{ marginTop: 20 }}
          disabled={this.state.isProcessing}
        >
          {this.state.isProcessing ? 'Adding...' : 'Add info'}
        </button>
      </StyledForm>
    );
  }
}

const InjectedForm = injectStripe(Form);

export class AddBillinginfo extends React.Component {
  render() {
    return (
      <StripeProvider apiKey={KEY}>
        <Elements>
          <InjectedForm />
        </Elements>
      </StripeProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/buy/AddBillingInfo.tsx